import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from '../../store/user/user.selector';
import { setSystemNotification } from '../../store/system/system.action';
import {
  getFriends,
  getPendingFriendRequests,
  sendFriendRequest,
  acceptFriendRequest,
  removeFriend,
  findUserByUsername
} from '../../utils/firebase/friends';

// styles
import {
  FriendsPageContainer,
  HeaderTopBar,
  FriendsContent,
  Section,
  FriendList,
  FriendCard,
  FriendInfo,
  ActionButtons,
  SearchBarComponent,
  EmptyState
} from './FriendsPage.styles.js';

// components
import LoadingIcon from '../../components/loading-icon/LoadingIcon';
import PrimaryBtn from '../../components/btns/primary-btn/PrimaryBtn';
import SecondaryBtn from '../../components/btns/secondary-btn/SecondaryBtn';
import InputBar from '../../components/input-bar/InputBar';
import HeaderWidget from '../../components/widgets/header-widget/HeaderWidget';
import Title from '../../components/widgets/components/title/Title';

export default function FriendsPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const [friends, setFriends] = useState([]);
  const [pendingRequests, setPendingRequests] = useState({ sent: [], received: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [searchUsername, setSearchUsername] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'decline' | 'remove' | 'cancel', uid: string }

  const loadFriendsData = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const [friendsList, requests] = await Promise.all([
        getFriends(user.uid),
        getPendingFriendRequests(user.uid)
      ]);
      setFriends(friendsList);
      setPendingRequests(requests);
    } catch (error) {
      console.error('Error loading friends data:', error);
      dispatch(setSystemNotification('Error loading friends data'));
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, user]);

  useEffect(() => {
    if (user) {
      loadFriendsData();
    }
  }, [user, loadFriendsData]);

  const handleSearchUser = async () => {
    if (!searchUsername.trim()) {
      setSearchResult(null);
      return;
    }

    setIsSearching(true);
    try {
      const foundUser = await findUserByUsername(searchUsername.trim());
      if (foundUser) {
        // Check if this is the current user
        if (foundUser.uid === user.uid) {
          setSearchResult({ ...foundUser, isSelf: true });
        } else {
          // Check if already a friend
          const isAlreadyFriend = friends.some(f => f.uid === foundUser.uid);
          const isPendingSent = pendingRequests.sent.some(r => r.uid === foundUser.uid);
          const isPendingReceived = pendingRequests.received.some(r => r.uid === foundUser.uid);

          setSearchResult({
            ...foundUser,
            isAlreadyFriend,
            isPendingSent,
            isPendingReceived
          });
        }
      } else {
        setSearchResult({ notFound: true });
      }
    } catch (error) {
      console.error('Error searching for user:', error);
      dispatch(setSystemNotification('Error searching for user'));
    } finally {
      setIsSearching(false);
    }
  };

  const handleSendFriendRequest = async (friendUID) => {
    setIsProcessing(true);
    try {
      await sendFriendRequest(user.uid, friendUID);
      await loadFriendsData();
      setSearchResult(null);
      setSearchUsername('');
      dispatch(setSystemNotification('Friend request sent!'));
    } catch (error) {
      console.error('Error sending friend request:', error);
      dispatch(setSystemNotification(error.message || 'Error sending friend request'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAcceptFriendRequest = async (friendUID) => {
    setIsProcessing(true);
    try {
      await acceptFriendRequest(user.uid, friendUID);
      await loadFriendsData();
      dispatch(setSystemNotification('Friend request accepted!'));
    } catch (error) {
      console.error('Error accepting friend request:', error);
      dispatch(setSystemNotification('Error accepting friend request'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemoveFriend = async (friendUID) => {
    setConfirmAction(null);
    setIsProcessing(true);
    try {
      await removeFriend(user.uid, friendUID);
      await loadFriendsData();
      dispatch(setSystemNotification('Friend removed'));
    } catch (error) {
      console.error('Error removing friend:', error);
      dispatch(setSystemNotification('Error removing friend'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelRequest = async (friendUID) => {
    setConfirmAction(null);
    setIsProcessing(true);
    try {
      await removeFriend(user.uid, friendUID);
      await loadFriendsData();
      setSearchResult((prev) =>
        prev?.uid === friendUID ? { ...prev, isPendingSent: false } : prev
      );
      dispatch(setSystemNotification('Request cancelled'));
    } catch (error) {
      console.error('Error cancelling request:', error);
      dispatch(setSystemNotification('Error cancelling request'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleViewCollection = (friendUID, friendUsername) => {
    navigate(`/friends/${friendUID}/collection`);
  };

  if (isLoading) {
    return (
      <FriendsPageContainer>
        <FriendsContent className='widget-bg'>
          <LoadingIcon />
        </FriendsContent>
      </FriendsPageContainer>
    );
  }

  return (
    <FriendsPageContainer>
      <FriendsContent>
        {/* New Friend Request - above HeaderWidget */}
        {pendingRequests.received.length > 0 && (
          <Section>
            <h2>NEW REQUEST</h2>
            <FriendList>
              {pendingRequests.received.map((request) => (
                <FriendCard key={request.uid} className='widget-bg'>
                  <FriendInfo>
                    <div>
                      <h3>@{request.username || 'No username'}</h3>
                    </div>
                  </FriendInfo>
                  <ActionButtons>
                    {confirmAction?.type === 'decline' && confirmAction?.uid === request.uid ? (
                      <>
                        <PrimaryBtn
                          type="critical"
                          title="CONFIRM DECLINE"
                          onClick={() => handleRemoveFriend(request.uid)}
                          disabled={isProcessing}
                          isLoading={isProcessing}
                        />
                        <SecondaryBtn
                          title="CANCEL"
                          onClick={() => setConfirmAction(null)}
                          isDisabled={isProcessing}
                        />
                      </>
                    ) : (
                      <>
                        <PrimaryBtn
                          title="ACCEPT"
                          onClick={() => handleAcceptFriendRequest(request.uid)}
                          disabled={isProcessing}
                          isLoading={isProcessing}
                        />
                        <PrimaryBtn
                          type="default"
                          title="DECLINE"
                          onClick={() => setConfirmAction({ type: 'decline', uid: request.uid })}
                          disabled={isProcessing}
                        />
                      </>
                    )}
                  </ActionButtons>
                </FriendCard>
              ))}
            </FriendList>
          </Section>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
        <HeaderWidget fullWidth={true}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
            <HeaderTopBar>
              <Title text={`FRIENDS (${friends.length})`} />
              <SearchBarComponent>
                <InputBar
                  label="Search username..."
                  value={searchUsername}
                  updateValue={setSearchUsername}
                  placeholder="Enter username"
                  expands={false}
                  prefix="@"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSearchUser();
                    }
                  }}
                />
                {searchUsername !== '' && (
                  <PrimaryBtn
                    title="SEARCH"
                    onClick={handleSearchUser}
                    disabled={isSearching || isProcessing}
                    isLoading={isSearching}
                  />
                )}
              </SearchBarComponent>
            </HeaderTopBar>
            {searchResult && (
              searchResult.notFound ? (
                <p style={{ width: '100%', textAlign: 'end', padding: "0px 32px", boxSizing: 'border-box' }}>User not found</p>
              ) : searchResult.isSelf ? (
                <p style={{ width: '100%', textAlign: 'end', padding: "0px 16px", boxSizing: 'border-box' }}>This is your account</p>
              ) : (
                <FriendCard className='widget-bg'>
                    <FriendInfo>
                      <div>
                        <h3>@{searchResult.username || 'No username'}</h3>
                      </div>
                    </FriendInfo>
                    <ActionButtons>
                      {searchResult.isAlreadyFriend ? (
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <PrimaryBtn
                            title="VIEW"
                            onClick={() => handleViewCollection(searchResult.uid, searchResult.username)}
                            disabled={isProcessing}
                          />
                          {confirmAction?.type === 'remove' && confirmAction?.uid === searchResult.uid ? (
                            <>
                              <PrimaryBtn
                                type="critical"
                                title="CONFIRM REMOVE"
                                onClick={() => handleRemoveFriend(searchResult.uid)}
                                disabled={isProcessing}
                                isLoading={isProcessing}
                              />
                              <SecondaryBtn
                                title="CANCEL"
                                onClick={() => setConfirmAction(null)}
                                isDisabled={isProcessing}
                              />
                            </>
                          ) : (
                            <PrimaryBtn
                              type="critical"
                              title="REMOVE FRIEND"
                              onClick={() => setConfirmAction({ type: 'remove', uid: searchResult.uid })}
                              disabled={isProcessing}
                            />
                          )}
                        </div>
                      ) : searchResult.isPendingSent ? (
                          confirmAction?.type === 'cancel' && confirmAction?.uid === searchResult.uid ? (
                            <>
                              <PrimaryBtn
                                type="critical"
                                title="CONFIRM CANCEL"
                                onClick={() => handleCancelRequest(searchResult.uid)}
                                disabled={isProcessing}
                                isLoading={isProcessing}
                              />
                              <SecondaryBtn
                                title="CANCEL"
                                onClick={() => setConfirmAction(null)}
                                isDisabled={isProcessing}
                              />
                            </>
                          ) : (
                          <PrimaryBtn
                            type="critical"
                            title="CANCEL REQUEST"
                            onClick={() => setConfirmAction({ type: 'cancel', uid: searchResult.uid })}
                            disabled={isProcessing}
                          />
                          )
                      ) : searchResult.isPendingReceived ? (
                        <PrimaryBtn
                          title="ACCEPT"
                          onClick={() => handleAcceptFriendRequest(searchResult.uid)}
                          disabled={isProcessing}
                          isLoading={isProcessing}
                        />
                      ) : (
                        <PrimaryBtn
                          title="SEND REQUEST"
                          onClick={() => handleSendFriendRequest(searchResult.uid)}
                          disabled={isProcessing}
                          isLoading={isProcessing}
                        />
                      )}
                    </ActionButtons>
                </FriendCard>
              )
            )}
          </div>
        </HeaderWidget>

        {/* Friends List */}
        <Section>
          {friends.length === 0 ? (
            <EmptyState>
              <p>No friends yet. Search for users above to add friends!</p>
            </EmptyState>
          ) : (
            <FriendList>
              {friends.map((friend) => (
                <FriendCard key={friend.uid} className='widget-bg'>
                  <FriendInfo>
                    <div>
                      <h3>{friend.username || 'No username'}</h3>
                      {friend.collectionCardCount !== undefined && (
                        <h5>{friend.collectionCardCount} cards</h5>
                      )}
                    </div>
                  </FriendInfo>
                  <ActionButtons>
                    <PrimaryBtn
                      title="VIEW"
                      onClick={() => handleViewCollection(friend.uid, friend.username)}
                      disabled={isProcessing}
                    />
                    {confirmAction?.type === 'remove' && confirmAction?.uid === friend.uid ? (
                      <>
                        <PrimaryBtn
                          type="critical"
                          title="CONFIRM REMOVE"
                          onClick={() => handleRemoveFriend(friend.uid)}
                          disabled={isProcessing}
                          isLoading={isProcessing}
                        />
                        <SecondaryBtn
                          title="CANCEL"
                          onClick={() => setConfirmAction(null)}
                          isDisabled={isProcessing}
                        />
                      </>
                    ) : (
                      <PrimaryBtn
                        type="default"
                        title="REMOVE"
                        onClick={() => setConfirmAction({ type: 'remove', uid: friend.uid })}
                        disabled={isProcessing}
                      />
                    )}
                  </ActionButtons>
                </FriendCard>
              ))}
            </FriendList>
          )}
        </Section>

        </div>

        {/* Sent Requests - below Friends list */}
        {pendingRequests.sent.length > 0 && (
          <Section>
            <h2>SENT REQUESTS</h2>
            <FriendList>
              {pendingRequests.sent.map((request) => (
                <FriendCard key={request.uid} className='widget-bg'>
                  <FriendInfo>
                    <div>
                      <h3>@{request.username || 'No username'}</h3>
                    </div>
                  </FriendInfo>
                  <ActionButtons>
                    {confirmAction?.type === 'cancel' && confirmAction?.uid === request.uid ? (
                      <>
                        <PrimaryBtn
                          type="critical"
                          title="CONFIRM CANCEL"
                          onClick={() => handleCancelRequest(request.uid)}
                          disabled={isProcessing}
                          isLoading={isProcessing}
                        />
                        <SecondaryBtn
                          title="CANCEL"
                          onClick={() => setConfirmAction(null)}
                          isDisabled={isProcessing}
                        />
                      </>
                    ) : (
                      <PrimaryBtn
                        type="critical"
                        title="CANCEL REQUEST"
                        onClick={() => setConfirmAction({ type: 'cancel', uid: request.uid })}
                        disabled={isProcessing}
                      />
                    )}
                  </ActionButtons>
                </FriendCard>
              ))}
            </FriendList>
          </Section>
        )}
      </FriendsContent>
    </FriendsPageContainer>
  );
}

